import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import cx from 'classnames';
import type { GroupBase, Props as ReactSelectProps } from 'react-select';
import css from './Select.module.less';

type AdditionalProps = {
  /**
   * Whether or not the left/right corners should not have a radius applied to them. Useful for when the select will be
   * right up against another component.
   */
  smooth?: 'left' | 'right';
  /**
   * Should the user be allowed to create their own values.
   */
  isCreatable?: boolean;
};

export type SelectProps<Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>> = Omit<ReactSelectProps<Option, IsMulti, Group>, 'onChange' | 'classNamePrefix'> & AdditionalProps & {
  id: string;
  // We have to override onChange to make it less miserable to work with.
  onChange: IsMulti extends true ? (e: Option[]) => void : (e: Option | null) => void;
};

/**
 * A wrapped react-select select dropdown that provides our FOSSA stylings and a couple of QoL typing changes.
 */
const Select = <Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
  props: SelectProps<Option, IsMulti, Group>,
) => {
  const Component = props.isCreatable === true ? CreatableSelect : ReactSelect;
  return (
    <Component
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      onChange={props.onChange as ReactSelectProps<Option, IsMulti, Group>['onChange']}
      className={cx(css.fossaSelect, props.className, {
        [css.smoothLeft]: props.smooth === 'left',
        [css.smoothRight]: props.smooth === 'right',
      })}
      //  Note: we target these classes by using the :global() selector in Select.module.less
      classNamePrefix="fossa-select"
    />
  );
};

export default Select;
